import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { ReportTable, Caveats, AdminReportGate, reportGet } from "@/components/reports/report-primitives";
import { CountBarChart } from "@/components/reports/report-charts";

export default function ComplianceReportPage() {
  usePageTitle("Compliance");
  const [, setLocation] = useLocation();
  const proc = useQuery({ queryKey: ["/api/reports/process-exceptions"], queryFn: () => reportGet("/api/reports/process-exceptions") });
  const docs = useQuery({ queryKey: ["/api/reports/document-requests"], queryFn: () => reportGet("/api/reports/document-requests") });
  const docRows = useQuery({ queryKey: ["/api/reports/document-request-rows", 31], queryFn: () => reportGet("/api/reports/document-request-rows?days=31") });

  const rows: any[] = proc.data?.rows ?? [];
  const staleDocs: any[] = docRows.data?.rows ?? [];

  useEffect(() => {
    const id = window.location.hash.replace(/^#/, "");
    if (!id) return;
    const t = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(t);
  }, [docRows.data]);

  return (
    <AdminReportGate>
    <ResponsiveLayout title="Compliance" subtitle="Work that finished with loose ends"
      breadcrumbs={[{ label: "Reports", href: "/reports" }, { label: "Compliance" }]}
      backTo={{ label: "All reports", href: "/reports" }}>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
          Every rule here is deterministic. “No note near completion” means no non-deleted work-order
          note within the window before the completing transition — it does not judge whether the note
          was any good.
        </div>

        <h2 className="text-xl font-medium mt-10 mb-4">Process exceptions</h2>
        <CountBarChart label="Orders" color="#fb7185"
          rows={rows.map(r => ({ label: r.exception, value: r.count }))} />

        <div className="mt-8">
          <ReportTable
            columns={[
              { key: "exception", header: "Exception",
                render: (r: any) => (<><div>{r.exception}</div><div className="text-xs text-gray-500">{r.detail}</div></>) },
              { key: "count", header: "Count", align: "right" },
            ]}
            rows={rows}
          />
        </div>

        <h2 id="docs-31" className="text-xl font-medium mt-12 mb-4 scroll-mt-20">Outstanding document requests</h2>
        <CountBarChart label="Requests" color="#f59e0b"
          rows={(docs.data?.rows ?? []).map((r: any) => ({ label: r.band, value: r.requests }))} />

        <h3 className="text-base font-medium mt-8 mb-1">31+ days</h3>
        <p className="text-sm text-gray-500 mb-4">
          {staleDocs.length} required documents still outstanding after a month.
        </p>
        <ReportTable
          columns={[
            { key: "workOrderNumber", header: "Work order" },
            { key: "companyName", header: "Client" },
            { key: "documentType", header: "Document" },
            { key: "description", header: "Description", render: (r: any) => r.description ?? "—" },
            { key: "currentAssignee", header: "Owner", render: (r: any) => r.currentAssignee ?? "—" },
            { key: "ageDays", header: "Outstanding", align: "right", render: (r: any) => `${Math.round(r.ageDays)}d` },
          ]}
          rows={staleDocs.slice(0, 50)}
          onRowClick={(r: any) => { setLocation(`/work-orders/${r.workOrderId}`); }}
          emptyMessage="No required document request is 31 days old."
        />
        {staleDocs.length > 0 && (
          <a href="/api/reports/document-request-rows.csv?days=31" className="text-sm text-indigo-600 hover:underline mt-3 inline-block">
            Download 31+ day document requests as CSV
          </a>
        )}

        <div className="mt-6">
          <a href="/api/reports/process-exceptions.csv" className="text-sm text-indigo-600 hover:underline">Download CSV</a>
        </div>
        <Caveats items={proc.data?.meta?.caveats ?? []} />
    </ResponsiveLayout>
    </AdminReportGate>
  );
}
